/**
 * Route-level error boundary.
 *
 * Wraps page content so a runtime exception in a single component (e.g.
 * a stray undefined reference in ProductDetail) never blanks the whole
 * site — the user gets a recovery message + a path back to /shop, and the
 * exception is surfaced to the browser console for triage.
 */
import React from "react";

export default class RouteErrorBoundary extends React.Component {
  constructor(props) {
    super(props);
    this.state = { error: null };
  }

  static getDerivedStateFromError(error) {
    return { error };
  }

  componentDidCatch(error, info) {
    // Surface to the console so production debugging is possible; never
    // swallow silently.
    // eslint-disable-next-line no-console
    console.error("[RouteErrorBoundary]", error, info?.componentStack);
  }

  render() {
    if (this.state.error) {
      return (
        <div className="min-h-screen bg-[#0a0a0a] text-white flex items-center justify-center px-6">
          <div className="max-w-md text-center" data-testid="route-error-boundary">
            <div className="text-[10px] uppercase tracking-[0.3em] text-red-500 font-bold font-mono mb-3">
              Page failed to load
            </div>
            <h1 className="font-display text-3xl tracking-tighter mb-3">
              We hit an unexpected error
            </h1>
            <p className="text-zinc-400 text-sm mb-6">
              Something on this page broke during render. The rest of the site is fine — try going back to the catalog.
            </p>
            <div className="flex items-center justify-center gap-3">
              <a href="/shop" className="px-5 h-10 inline-flex items-center bg-red-600 hover:bg-red-700 text-white text-sm rounded-sm">
                Back to shop
              </a>
              <button
                onClick={() => this.setState({ error: null })}
                className="px-5 h-10 inline-flex items-center border border-white/10 hover:bg-white/5 text-zinc-300 text-sm rounded-sm"
              >
                Retry
              </button>
            </div>
          </div>
        </div>
      );
    }
    return this.props.children;
  }
}
